import { cn } from "./cn";
import type { Tone } from "./StatusBadge";

const FILLS: Record<Tone, string> = {
  green: "bg-emerald-500",
  red: "bg-red-500",
  amber: "bg-amber-500",
  blue: "bg-brand-500",
  violet: "bg-violet-500",
  gray: "bg-ink-400",
};

/**
 * A proportion, shown as a bar and stated in words.
 *
 * <p>The label is not optional decoration. A bar alone says "roughly two thirds" and nothing more,
 * and on a release page the reader's next question is always "two thirds of what" — so the count
 * travels with it. §20 makes the same argument about readiness: a shape without a number is a
 * thing you cannot act on.
 *
 * <p>Zero of zero renders as an empty track rather than a full one. An empty release is not
 * finished, and dividing by nothing to get 100% is the sort of arithmetic that makes somebody
 * distrust the whole screen.
 */
export function ProgressBar({
  value,
  total,
  tone = "blue",
  label,
  className,
}: {
  value: number;
  total: number;
  tone?: Tone;
  label?: string;
  className?: string;
}) {
  const fraction = total > 0 ? Math.min(1, Math.max(0, value / total)) : 0;
  const percent = Math.round(fraction * 100);

  return (
    <div className={className}>
      {label != null && (
        <div className="mb-1 flex items-baseline justify-between gap-2">
          <span className="text-xs text-ink-600">{label}</span>
          <span className="tnum text-xs font-medium text-ink-800">
            {value} / {total}
          </span>
        </div>
      )}
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-ink-100"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? `${value} of ${total}`}
      >
        <div
          className={cn("h-full rounded-full transition-[width] duration-300", FILLS[tone])}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
